import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Receipt, Search, Printer, AlertTriangle, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { InvoiceChargesSection, recalcInvoice, EXTRA_CHARGE_FIELDS } from "../components/invoices/InvoiceChargesSection";
import { InvoiceRowActions, isInvoiceOverdue, daysOverdue } from "../components/invoices/InvoiceRowActions";
import { InvoicePaymentDialog } from "../components/invoices/InvoicePaymentDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  date: new Date().toISOString().split('T')[0],
  client_id: "",
  client_name: "",
  client_address: "",
  client_gst: "",
  load_slip_ids: [],
  items: [],
  subtotal: 0,
  halting_charge: 0,
  loading_unloading_charge: 0,
  toll_charge: 0,
  gst_type: "sgst_cgst",
  sgst_percentage: 9,
  cgst_percentage: 9,
  igst_percentage: 0,
  sgst_amount: 0,
  cgst_amount: 0,
  igst_amount: 0,
  gst_percentage: 18,
  gst_amount: 0,
  total_amount: 0,
  due_date: "",
  payment_terms: "Net 30",
  notes: ""
});

const EDITABLE_FIELDS = [
  "subtotal", "gst_type", "sgst_percentage", "cgst_percentage", "igst_percentage",
  "sgst_amount", "cgst_amount", "igst_amount", "gst_percentage", "gst_amount", "total_amount",
  "due_date", "payment_terms", "notes", ...EXTRA_CHARGE_FIELDS.map(f => f.key)
];

const InvoicesPage = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadSlips, setLoadSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedSlips, setSelectedSlips] = useState([]);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient && !editingInvoice) {
      fetchClientLoadSlips(selectedClient);
    }
  }, [selectedClient, editingInvoice]);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/invoices`),
        axios.get(`${API_URL}/clients`)
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data.filter(c => c.status === "active"));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientLoadSlips = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/load-slips?client_id=${clientId}&invoiced=false`);
      setLoadSlips(response.data.filter(s => s.status === "delivered"));
    } catch (error) {
      toast.error("Failed to fetch load slips");
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(clientId);
    setSelectedSlips([]);
    setFormData(recalcInvoice({
      ...formData,
      client_id: clientId,
      client_name: client?.company_name || "",
      client_address: client?.address || "",
      client_gst: client?.gst_number || "",
      load_slip_ids: [],
      items: [],
      subtotal: 0
    }));
  };

  const handleSlipToggle = (slipId) => {
    const newSelectedSlips = selectedSlips.includes(slipId)
      ? selectedSlips.filter(id => id !== slipId)
      : [...selectedSlips, slipId];
    setSelectedSlips(newSelectedSlips);

    const items = newSelectedSlips.map(id => {
      const slip = loadSlips.find(s => s.id === id);
      return {
        description: `${slip.material_type} - ${slip.origin} to ${slip.destination} (${slip.slip_number})`,
        quantity: slip.quantity,
        rate: slip.freight_amount / slip.quantity,
        amount: slip.freight_amount
      };
    });
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    setFormData(recalcInvoice({ ...formData, load_slip_ids: newSelectedSlips, items, subtotal }));
  };

  const openCreate = () => {
    setEditingInvoice(null);
    setSelectedClient("");
    setSelectedSlips([]);
    setLoadSlips([]);
    setFormData(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (invoice) => {
    setEditingInvoice(invoice);
    setSelectedClient(invoice.client_id);
    setSelectedSlips(invoice.load_slip_ids || []);
    setFormData(recalcInvoice({ ...emptyForm(), ...invoice }));
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvoice) {
        const payload = Object.fromEntries(EDITABLE_FIELDS.map(k => [k, formData[k]]));
        await axios.put(`${API_URL}/invoices/${editingInvoice.id}`, payload);
        toast.success("Invoice updated successfully");
      } else {
        if (selectedSlips.length === 0) {
          toast.error("Please select at least one load slip");
          return;
        }
        await axios.post(`${API_URL}/invoices`, formData);
        toast.success("Invoice created successfully");
      }
      setDialogOpen(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice");
    }
  };

  const handleStatusChange = async (invoice, status) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoice.id}`, { status });
      toast.success(`Invoice marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handlePaymentSaved = (updated) => {
    setInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? updated : inv)));
    setPaymentInvoice(updated);
  };

  const handlePreview = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.id}/html`);
      setPreviewHtml(response.data);
      setSelectedInvoice(invoice);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load preview");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "border-gray-500/50 text-gray-400 bg-gray-500/10",
      sent: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      partially_paid: "border-amber-500/50 text-amber-400 bg-amber-500/10",
      paid: "border-green-500/50 text-green-400 bg-green-500/10",
      cancelled: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.draft;
  };

  const canCreate = user?.role === "admin" || user?.role === "super_admin";

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const overdueInvoices = invoices.filter(isInvoiceOverdue);
  const overdueTotal = overdueInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Receipt className="w-8 h-8 text-chart-3" />
            Invoice Management
          </h1>
          <p className="text-muted-foreground mt-1">Generate invoices from delivered load slips</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="aging-report-btn">
            <Link to="/dashboard/invoices/aging"><TrendingDown className="w-4 h-4 mr-2" />Aging Report</Link>
          </Button>
          {canCreate && (
            <Button 
              onClick={openCreate}
              className="glow-primary bg-primary hover:bg-primary/90"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          )}
        </div>
      </div>

      {overdueInvoices.length > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300" data-testid="overdue-banner">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm">
            <span className="font-semibold" data-testid="overdue-count">{overdueInvoices.length} overdue invoice{overdueInvoices.length > 1 ? "s" : ""}</span>
            {" "}totaling <span className="font-mono font-semibold" data-testid="overdue-total">{formatCurrency(overdueTotal)}</span>. Use the bell icon to email a payment reminder or{" "}
            <Link to="/dashboard/invoices/aging" className="underline hover:text-red-200" data-testid="overdue-aging-link">view the aging report</Link>.
          </p>
        </div>
      )}

      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by invoice number or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="invoice-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Invoices ({filteredInvoices.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Invoice No.</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Date</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Client</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Amount</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Due Date</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No invoices found. Create your first invoice to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const overdue = isInvoiceOverdue(invoice);
                  return (
                  <TableRow key={invoice.id} className={`border-border table-row-hover ${overdue ? "bg-red-500/10 hover:bg-red-500/15" : ""}`} data-testid={`invoice-row-${invoice.id}`} data-overdue={overdue}>
                    <TableCell className="font-mono font-bold text-primary">{invoice.invoice_number}</TableCell>
                    <TableCell className="text-muted-foreground">{invoice.date}</TableCell>
                    <TableCell className="text-foreground">{invoice.client_name}</TableCell>
                    <TableCell className="font-mono text-foreground" data-testid={`invoice-amount-${invoice.id}`}>
                      {formatCurrency(invoice.total_amount)}
                      {invoice.amount_paid > 0 && invoice.status !== "paid" && (
                        <span className="block text-xs text-amber-400" data-testid={`invoice-balance-${invoice.id}`}>
                          Paid {formatCurrency(invoice.amount_paid)} · Due {formatCurrency(invoice.total_amount - invoice.amount_paid)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={overdue ? "text-red-400 font-semibold" : "text-muted-foreground"} data-testid={`invoice-due-${invoice.id}`}>
                      {invoice.due_date}
                      {overdue && <span className="block text-xs font-normal">{daysOverdue(invoice)} days late</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge variant="outline" className={getStatusBadge(invoice.status)} data-testid={`invoice-status-${invoice.id}`}>
                          {invoice.status.replace("_", " ")}
                        </Badge>
                        {overdue && (
                          <Badge variant="outline" className="border-red-500/50 text-red-400 bg-red-500/10" data-testid={`invoice-overdue-${invoice.id}`}>
                            overdue
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <InvoiceRowActions
                        invoice={invoice}
                        canManage={canCreate}
                        onPreview={handlePreview}
                        onEdit={openEdit}
                        onStatusChange={handleStatusChange}
                        onRecordPayment={setPaymentInvoice}
                        onRefresh={fetchData}
                      />
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingInvoice(null); }}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" data-testid="invoice-dialog-title">
              {editingInvoice ? `Edit Invoice ${editingInvoice.invoice_number}` : "Create Invoice"}
            </DialogTitle>
            <DialogDescription>
              {editingInvoice ? "Adjust charges, GST and due date on this draft invoice" : "Generate an invoice from delivered load slips"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Invoice Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  disabled={!!editingInvoice}
                  data-testid="invoice-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="invoice-due-date-input"
                />
              </div>
            </div>

            {editingInvoice ? (
              <div className="space-y-2">
                <Label>Client</Label>
                <div className="p-3 rounded-lg bg-secondary/30 border border-border text-sm" data-testid="invoice-edit-client">
                  <span className="font-semibold text-foreground">{formData.client_name}</span>
                  {formData.client_gst && <span className="text-muted-foreground ml-2">GST: {formData.client_gst}</span>}
                </div>
                <Label>Invoice Items</Label>
                <div className="border border-border rounded-lg p-3 space-y-1 bg-secondary/30 text-sm" data-testid="invoice-edit-items">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between gap-3">
                      <span className="text-muted-foreground truncate">{item.description}</span>
                      <span className="font-mono text-foreground whitespace-nowrap">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="client">Select Client</Label>
                  <Select value={selectedClient} onValueChange={handleClientChange}>
                    <SelectTrigger className="bg-secondary/50 border-border" data-testid="invoice-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedClient && loadSlips.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select Delivered Load Slips</Label>
                    <div className="border border-border rounded-lg p-4 space-y-2 max-h-[200px] overflow-y-auto bg-secondary/30">
                      {loadSlips.map((slip) => (
                        <label key={slip.id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSlips.includes(slip.id)}
                            onChange={() => handleSlipToggle(slip.id)}
                            className="rounded"
                            data-testid={`invoice-slip-checkbox-${slip.id}`}
                          />
                          <div className="flex-1">
                            <span className="font-mono text-primary text-sm">{slip.slip_number}</span>
                            <span className="text-muted-foreground text-sm ml-2">{slip.origin} → {slip.destination}</span>
                          </div>
                          <span className="font-mono text-foreground">{formatCurrency(slip.freight_amount)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {selectedClient && loadSlips.length === 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
                    No delivered load slips available for this client.
                  </div>
                )}
              </>
            )}

            {formData.items.length > 0 && (
              <InvoiceChargesSection formData={formData} onChange={setFormData} formatCurrency={formatCurrency} />
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Thank you for your business!"
                data-testid="invoice-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                type="submit" 
                className="glow-primary bg-primary" 
                disabled={!editingInvoice && selectedSlips.length === 0}
                data-testid="invoice-submit-btn"
              >
                {editingInvoice ? "Save Changes" : "Create Invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <InvoicePaymentDialog
        invoice={paymentInvoice}
        open={!!paymentInvoice}
        onClose={() => setPaymentInvoice(null)}
        onSaved={handlePaymentSaved}
      />

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-white text-black max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-black flex items-center justify-between">
              <span>Invoice Preview - {selectedInvoice?.invoice_number}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-invoice">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button size="sm" className="bg-primary" data-testid="preview-download-pdf"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/invoices/${selectedInvoice.id}/pdf`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url; a.download = `${selectedInvoice.invoice_number}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                    } catch (error) { toast.error("Failed to download PDF"); }
                  }}>
                  Download PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
